// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type { Appointment, Practitioner } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';

interface UseProviderAppointmentsOptions {
  clinicView?: boolean;
  recentDays?: number | null;
}

/**
 * Statuses for an appointment that is still live — not yet concluded, cancelled,
 * or no-showed. A virtual clinic has no in-person 'arrived'/'checked-in' states,
 * so a booked appointment stays booked until it is concluded.
 */
const ACTIVE_STATUSES = ['booked'];

export interface UseProviderAppointmentsReturn {
  /** Today's booked appointments */
  todayAppointments: Appointment[];
  /** Last 30 days of appointments with a recorded outcome. */
  recentAppointments: Appointment[];
  /** Whether appointments are being loaded */
  loading: boolean;
  /** Any error that occurred during fetching */
  error: Error | null;
  /** Re-fetch appointments */
  refresh: () => void;
}

/**
 * Hook that fetches the current practitioner's appointments and splits them into
 * today's active appointments and recent (last 30 days) appointments that may still
 * owe a note. The two lists are disjoint: an active appointment belongs to today's
 * list until its day is over, after which it moves to recent.
 * @returns Today's appointments, recent appointments, loading state, error, and refresh function
 */
export function useProviderAppointments(options?: UseProviderAppointmentsOptions): UseProviderAppointmentsReturn {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const clinicView = options?.clinicView ?? false;
  const recentDays = options?.recentDays === undefined ? 30 : options.recentDays;
  const [todayAppointments, setTodayAppointments] = useState<Appointment[]>([]);
  const [recentAppointments, setRecentAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchAppointments = useCallback(async () => {
    if (!clinicView && !profile?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const practitionerRef = profile ? (getReferenceString(profile) as string) : undefined;
      const now = new Date();

      // Today's date boundaries
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);

      // 30 days ago for recent appointments
      const recentStart = recentDays === null ? undefined : new Date(now);
      if (recentStart && recentDays !== null) {
        recentStart.setDate(recentStart.getDate() - recentDays);
      }

      // Build search params with URLSearchParams to support duplicate 'date' keys
      const todayParams = new URLSearchParams();
      if (practitionerRef && !clinicView) {
        todayParams.append('actor', practitionerRef);
      }
      todayParams.append('date', `ge${startOfToday.toISOString()}`);
      todayParams.append('date', `le${endOfToday.toISOString()}`);
      todayParams.append('_count', '100');
      todayParams.append('_sort', 'date');

      const recentParams = new URLSearchParams();
      if (practitionerRef && !clinicView) {
        recentParams.append('actor', practitionerRef);
      }
      if (recentStart) {
        recentParams.append('date', `ge${recentStart.toISOString()}`);
      }
      // Bound on end-of-today, not `now`: a session concluded before its scheduled
      // start time is fulfilled but still has a future `date`, and would otherwise
      // fall out of today's list (no longer active) without landing in recent.
      recentParams.append('date', `le${endOfToday.toISOString()}`);
      recentParams.append('status', ['fulfilled', 'cancelled', 'noshow'].join(','));
      recentParams.append('_count', '100');
      recentParams.append('_sort', '-date');

      // Fetch today's appointments and recent appointments in parallel
      const [todayResults, recentResults] = await Promise.all([
        medplum.searchResources('Appointment', todayParams),
        medplum.searchResources('Appointment', recentParams),
      ]);

      // Filter today's to only active statuses
      const todayList = todayResults
        .filter((appt) => ACTIVE_STATUSES.includes(appt.status as string))
        .sort((a, b) => {
          const aTime = a.start ? new Date(a.start).getTime() : 0;
          const bTime = b.start ? new Date(b.start).getTime() : 0;
          return aTime - bTime;
        });

      // Past Appointments is an outcome history, so active/booked appointments stay
      // out of this list even when their scheduled time has elapsed.
      const recentList = recentResults
        .filter((appt) => {
          if (appt.status === 'fulfilled' || appt.status === 'cancelled' || appt.status === 'noshow') {
            return true;
          }
          return false;
        })
        .sort((a, b) => {
          const aTime = a.start ? new Date(a.start).getTime() : 0;
          const bTime = b.start ? new Date(b.start).getTime() : 0;
          return bTime - aTime;
        });

      setTodayAppointments(todayList);
      setRecentAppointments(recentList);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load appointments'));
    } finally {
      setLoading(false);
    }
  }, [clinicView, medplum, profile, recentDays]);

  useEffect(() => {
    fetchAppointments().catch(console.error);
  }, [fetchAppointments]);

  return { todayAppointments, recentAppointments, loading, error, refresh: fetchAppointments };
}
